import { BOOK_INFO, CHAPTERS } from "@/lib/index";
import { introContent, bookContent, authorNote, consultationSection, disclaimer } from "@/data/bookContent";
import { ContentItem, FlowchartStep } from "@/data/bookContent";

function PrintSectionHeading({ text }: { text: string }) {
  return (
    <h3 style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "1rem", fontWeight: 700, borderBottom: "1px solid #e5e7eb", paddingBottom: "0.4rem", marginTop: "1.8rem", marginBottom: "0.8rem", color: "#1a1a2e" }}>
      {text}
    </h3>
  );
}

function PrintParagraph({ text }: { text: string }) {
  return (
    <p style={{ fontFamily: "'Noto Serif JP', serif", lineHeight: 1.9, marginBottom: "0.8rem", fontSize: "0.9rem", color: "#2a2a3e" }}>
      {text}
    </p>
  );
}

function PrintListGood({ items }: { items: string[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "0.8rem 0" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.4rem", alignItems: "flex-start" }}>
          <span style={{ color: "#16a34a", fontSize: "0.85rem", flexShrink: 0, marginTop: "0.15rem" }}>✓</span>
          <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.85rem", lineHeight: 1.7, color: "#2a2a3e" }}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function PrintListBad({ items }: { items: string[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "0.8rem 0" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.4rem", alignItems: "flex-start" }}>
          <span style={{ color: "#dc2626", fontSize: "0.85rem", flexShrink: 0, marginTop: "0.15rem" }}>✗</span>
          <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.85rem", lineHeight: 1.7, color: "#2a2a3e" }}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function PrintListNumbered({ items }: { items: string[] }) {
  return (
    <ol style={{ listStyle: "none", padding: 0, margin: "0.8rem 0" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: "0.6rem", marginBottom: "0.5rem", alignItems: "flex-start" }}>
          <span style={{ background: "#1a1a2e", color: "#c9a84c", borderRadius: "50%", width: "1.4rem", height: "1.4rem", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, flexShrink: 0, marginTop: "0.1rem" }}>
            {i + 1}
          </span>
          <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.85rem", lineHeight: 1.7, color: "#2a2a3e" }}>{item}</span>
        </li>
      ))}
    </ol>
  );
}

function PrintListPlain({ items }: { items: string[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "0.8rem 0" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.4rem", alignItems: "flex-start" }}>
          <span style={{ color: "#c9a84c", fontSize: "0.85rem", flexShrink: 0, marginTop: "0.15rem" }}>›</span>
          <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.85rem", lineHeight: 1.7, color: "#2a2a3e" }}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function PrintCalloutInfo({ title, text }: { title: string; text: string }) {
  return (
    <div style={{ border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: "0.5rem", padding: "0.8rem 1rem", margin: "0.8rem 0" }}>
      {title && <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontWeight: 700, fontSize: "0.85rem", color: "#1e40af", marginBottom: text ? "0.3rem" : 0 }}>{title}</p>}
      {text && <p style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.85rem", lineHeight: 1.7, color: "#1e3a8a", margin: 0 }}>{text}</p>}
    </div>
  );
}

function PrintCalloutWarning({ title, text }: { title: string; text: string }) {
  return (
    <div style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "0.5rem", padding: "0.8rem 1rem", margin: "0.8rem 0" }}>
      {title && <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontWeight: 700, fontSize: "0.85rem", color: "#92400e", marginBottom: text ? "0.3rem" : 0 }}>{title}</p>}
      {text && <p style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.85rem", lineHeight: 1.7, color: "#78350f", margin: 0 }}>{text}</p>}
    </div>
  );
}

function PrintFlowchart({ steps }: { steps: FlowchartStep[] }) {
  return (
    <div style={{ margin: "0.8rem 0" }}>
      {steps.map((step) => (
        <div key={step.number} style={{ border: "1px solid #e5e7eb", borderRadius: "0.5rem", padding: "0.7rem 0.9rem", marginBottom: "0.5rem", background: "#fafafa" }}>
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
            <span style={{ background: "#1a1a2e", color: "#ffffff", borderRadius: "50%", width: "1.4rem", height: "1.4rem", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, flexShrink: 0 }}>
              {step.number}
            </span>
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#1a1a2e", marginBottom: "0.4rem" }}>
                {step.question}
              </p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <div style={{ flex: 1, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "0.35rem", padding: "0.3rem 0.5rem" }}>
                  <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#15803d", marginBottom: "0.15rem" }}>YES</p>
                  <p style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.7rem", color: "#166534", margin: 0 }}>{step.yes}</p>
                </div>
                <div style={{ flex: 1, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "0.35rem", padding: "0.3rem 0.5rem" }}>
                  <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#b91c1c", marginBottom: "0.15rem" }}>NO</p>
                  <p style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.7rem", color: "#991b1b", margin: 0 }}>{step.no}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PrintChecklistGroup({ title, items }: { title: string; items: string[] }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "0.5rem", marginBottom: "0.8rem", overflow: "hidden" }}>
      <div style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb", padding: "0.4rem 0.8rem" }}>
        <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.85rem", fontWeight: 700, color: "#1a1a2e", margin: 0 }}>{title}</p>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 0.8rem", borderBottom: i < items.length - 1 ? "1px solid #f3f4f6" : "none" }}>
            <span style={{ width: "1rem", height: "1rem", border: "1.5px solid #9ca3af", borderRadius: "0.2rem", display: "inline-block", flexShrink: 0 }} />
            <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.82rem", color: "#2a2a3e" }}>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderPrintItem(item: ContentItem, index: number) {
  switch (item.type) {
    case "section-heading": return <PrintSectionHeading key={index} text={item.text} />;
    case "paragraph": return <PrintParagraph key={index} text={item.text} />;
    case "list-good": return <PrintListGood key={index} items={item.items} />;
    case "list-bad": return <PrintListBad key={index} items={item.items} />;
    case "list-numbered": return <PrintListNumbered key={index} items={item.items} />;
    case "list-plain": return <PrintListPlain key={index} items={item.items} />;
    case "callout-info": return <PrintCalloutInfo key={index} title={item.title} text={item.text} />;
    case "callout-warning": return <PrintCalloutWarning key={index} title={item.title} text={item.text} />;
    case "flowchart": return <PrintFlowchart key={index} steps={item.steps} />;
    case "checklist-group": return <PrintChecklistGroup key={index} title={item.title} items={item.items} />;
    default: return null;
  }
}

export function PrintContent() {
  const baseFont: React.CSSProperties = { fontFamily: "'Noto Serif JP', serif" };
  const pageStyle: React.CSSProperties = {
    ...baseFont,
    maxWidth: "740px",
    margin: "0 auto",
    padding: "40px 48px",
    background: "#ffffff",
    color: "#1a1a2e",
  };

  return (
    <div id="print-content-root" className="print-only" style={pageStyle}>
      <div style={{ borderBottom: "3px solid #1a1a2e", paddingBottom: "2rem", marginBottom: "2rem" }}>
        <p style={{ fontSize: "0.75rem", letterSpacing: "0.15em", color: "#c9a84c", marginBottom: "0.75rem" }}>
          発行：{BOOK_INFO.publicationDate}
        </p>
        <h1 style={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.3, marginBottom: "0.5rem", color: "#1a1a2e" }}>
          {BOOK_INFO.title}
        </h1>
        <p style={{ fontSize: "1.1rem", color: "#c9a84c", marginBottom: "0.4rem" }}>
          {BOOK_INFO.subtitle}
        </p>
        <p style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "1.5rem" }}>
          {BOOK_INFO.tagline}
        </p>
        <p style={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a2e" }}>
          著者：{BOOK_INFO.author}
        </p>
        <p style={{ fontSize: "0.8rem", color: "#6b7280" }}>
          {BOOK_INFO.authorTitle}
        </p>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.3rem", fontWeight: 700, borderLeft: "4px solid #c9a84c", paddingLeft: "0.75rem", marginBottom: "1rem", color: "#1a1a2e" }}>
          はじめに：私の立場
        </h2>
        <div>
          {introContent.foreword.map((para, i) => (
            <PrintParagraph key={i} text={para} />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.3rem", fontWeight: 700, borderLeft: "4px solid #c9a84c", paddingLeft: "0.75rem", marginBottom: "1rem", color: "#1a1a2e" }}>
          この資料の結論
        </h2>
        <div>
          {introContent.conclusion.map((para, i) => (
            <PrintParagraph key={i} text={para} />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: "2.5rem", border: "1px solid #e5e7eb", borderRadius: "0.5rem", overflow: "hidden" }}>
        <div style={{ background: "#1a1a2e", padding: "0.6rem 1rem" }}>
          <p style={{ fontSize: "0.9rem", fontWeight: 700, color: "#ffffff", margin: 0 }}>目次</p>
        </div>
        {CHAPTERS.filter(ch => ch.id !== 'cover' && ch.id !== 'closing').map((ch) => (
          <div key={ch.id} style={{ display: "flex", gap: "1rem", padding: "0.5rem 1rem", borderBottom: "1px solid #f3f4f6" }}>
            <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#c9a84c", minWidth: "4rem" }}>
              第{ch.number}章
            </span>
            <span style={{ fontSize: "0.8rem", color: "#2a2a3e" }}>
              {ch.title}
            </span>
          </div>
        ))}
      </div>

      {bookContent.map((chapter) => (
        <div key={chapter.id} style={{ marginBottom: "2.5rem", paddingBottom: "1.5rem", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ marginBottom: "1.2rem" }}>
            <span style={{ background: "#1a1a2e", color: "#ffffff", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", padding: "0.25rem 0.75rem", borderRadius: "999px", display: "inline-block", marginBottom: "0.6rem" }}>
              第{CHAPTERS.find(c => c.id === chapter.id)?.number}章
            </span>
            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.3, color: "#1a1a2e", borderBottom: "2px solid #c9a84c", paddingBottom: "0.4rem", marginTop: "0.3rem" }}>
              {chapter.title}
            </h2>
          </div>
          <div>
            {chapter.content.map((item, i) => renderPrintItem(item, i))}
          </div>
        </div>
      ))}

      <div style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.3rem", fontWeight: 700, borderLeft: "4px solid #c9a84c", paddingLeft: "0.75rem", marginBottom: "1rem", color: "#1a1a2e" }}>
          著者からの一言
        </h2>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: "0.5rem", padding: "1.2rem 1.5rem", background: "#fafafa" }}>
          {authorNote.map((para, i) => (
            <PrintParagraph key={i} text={para} />
          ))}
          <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid #e5e7eb" }}>
            <p style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1a1a2e", marginBottom: "0.2rem" }}>
              {BOOK_INFO.author}
            </p>
            <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: 0 }}>
              {BOOK_INFO.authorTitle}
            </p>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.3rem", fontWeight: 700, borderLeft: "4px solid #c9a84c", paddingLeft: "0.75rem", marginBottom: "1rem", color: "#1a1a2e" }}>
          {consultationSection.title}
        </h2>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: "0.5rem", padding: "1.2rem 1.5rem", background: "#fafafa" }}>
          <PrintParagraph text={consultationSection.body} />
          <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid #e5e7eb" }}>
            <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#1a1a2e", marginBottom: "0.3rem" }}>LINE相談URL</p>
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.8rem", color: "#2563eb", margin: 0 }}>
              https://line.me/ti/p/@tes2030
            </p>
          </div>
        </div>
      </div>

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "0.5rem", padding: "1rem 1.2rem", marginBottom: "1.5rem" }}>
        <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.8rem", fontWeight: 700, color: "#1a1a2e", marginBottom: "0.4rem" }}>
          免責・情報基準日
        </p>
        <p style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "0.75rem", lineHeight: 1.7, color: "#6b7280", margin: 0 }}>
          {disclaimer}
        </p>
      </div>

      <div style={{ textAlign: "center", paddingTop: "1rem", borderTop: "1px solid #e5e7eb" }}>
        <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.75rem", color: "#9ca3af" }}>
          {BOOK_INFO.copyright}
        </p>
      </div>
    </div>
  );
}